import { useState } from "react";
import {
  ORDER_STATUSES,
  computeTotals,
  emptyItem,
  emptyOrder,
  formatINR,
  toAmount,
} from "../../api/orders";
import { Field, PanelMessage, SelectField, Spinner, formatDate } from "./shared";
import { BackIcon, ChevronIcon } from "./icons";
import styles from "./Admin.module.css";

/*
  Orders: what a client has taken (plan, software, service), the list price,
  the negotiated price, advance paid and status.
*/
// Ids come back from the API as numbers but <select> values are strings.
const sameId = (a, b) => String(a ?? "") === String(b ?? "");

export default function OrdersPanel({ orders, clients, draft, onDraftHandled }) {
  const [editing, setEditing] = useState(draft || null); // null | "new" | order object
  const [selectedId, setSelectedId] = useState(null); // order open in the detail view

  const closeForm = () => {
    setEditing(null);
    if (draft) onDraftHandled();
  };

  if (orders.error && !orders.items) {
    return (
      <PanelMessage>
        {orders.error} <button className={styles.linkBtn} onClick={orders.load}>Retry</button>
      </PanelMessage>
    );
  }
  if (orders.items === null || clients.items === null) return <PanelMessage><Spinner /></PanelMessage>;

  const visible = orders.items;
  const statusLabel = (v) => ORDER_STATUSES.find((s) => s.value === v)?.label || v;

  // Prefer the snapshot stored with the order; fall back to the live client
  // record for orders saved without one.
  const clientOf = (o) => {
    const snap = o.client || {};
    if (snap.name || snap.businessName) return snap;
    return clients.items.find((c) => sameId(c.id, o.clientId)) || snap;
  };

  const saveOrder = async (data) => {
    const client = clients.items.find((c) => sameId(c.id, data.clientId));
    if (editing === "new") await orders.add(data, client);
    else await orders.update(editing.id, data, client);
    closeForm();
  };

  const removeOrder = (o) => {
    if (!window.confirm(`Delete order ${o.orderNo}? This cannot be undone.`)) return;
    orders.remove(o.id);
    setSelectedId(null);
  };

  const selected = selectedId != null ? visible.find((o) => sameId(o.id, selectedId)) : null;

  // Detail view: one order, with edit / delete. Editing swaps in the form.
  if (selected) {
    return (
      <div className={styles.dash} key="detail">
        <button className={styles.backBtn} onClick={() => { setSelectedId(null); setEditing(null); }}>
          <BackIcon /> All orders
        </button>
        {editing && editing !== "new" ? (
          <OrderForm order={editing} clients={clients.items} onCancel={closeForm} onSave={saveOrder} />
        ) : (
          <OrderDetail
            order={selected}
            client={clientOf(selected)}
            statusLabel={statusLabel}
            onStatus={(status) => orders.setStatus(selected.id, status)}
            onEdit={() => setEditing(selected)}
            onDelete={() => removeOrder(selected)}
            onPayment={(advance) => orders.update(selected.id, { ...selected, advance }, clientOf(selected))}
          />
        )}
        {orders.error && <p className={styles.error}>{orders.error}</p>}
      </div>
    );
  }

  return (
    <div className={styles.dash} key="list">
      {editing && (
        <OrderForm
          order={editing === "new" ? emptyOrder : editing}
          clients={clients.items}
          onCancel={closeForm}
          onSave={saveOrder}
        />
      )}

      <div className={styles.toolbar}>
        <p className={styles.sub}>Click an order to see its details, edit it or delete it.</p>
        <div className={styles.headActions}>
          <button className={styles.ghostBtn} onClick={orders.load}>Refresh</button>
          <button
            className={styles.solidBtn}
            onClick={() => setEditing("new")}
            disabled={clients.items.length === 0}
            title={clients.items.length === 0 ? "Add a client first" : undefined}
          >
            + New order
          </button>
        </div>
      </div>

      {orders.error && <p className={styles.error}>{orders.error}</p>}

      {visible.length === 0 ? (
        <PanelMessage>
          {clients.items.length === 0 ? "Add a client first, then create their order." : "No orders yet."}
        </PanelMessage>
      ) : (
        <div className={`${styles.tableWrap} ${styles.stackWrap}`}>
          <table className={`${styles.table} ${styles.stackTable}`}>
            <thead>
              <tr>
                <th>Order</th>
                <th>Client</th>
                <th>Product / Service</th>
                <th className={styles.amt}>Amount</th>
                <th className={styles.amt}>Payment status</th>
                <th>Delivery</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => {
                const client = clientOf(o);
                const open = () => setSelectedId(o.id);
                return (
                <tr
                  key={o.id}
                  className={`${styles.rowLink} ${o.status === "cancelled" ? styles.rowClosed : ""}`}
                  onClick={open}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
                  tabIndex={0}
                  role="link"
                  aria-label={`Open order ${o.orderNo}`}
                >
                  <td className={styles.cellTitle}>
                    <strong>{o.orderNo}</strong>
                    <div className={styles.dim}>{formatDate(o.orderDate)}</div>
                  </td>
                  <td className={styles.cellWide}>
                    <strong>{client.name || client.businessName || "—"}</strong>
                    {client.name && client.businessName && <div className={styles.dim}>{client.businessName}</div>}
                  </td>
                  <td className={`${styles.itemsCell} ${styles.cellWide}`}>
                    <ul>
                      {(o.items || []).map((it, i) => (
                        <li key={i}>{it.name}</li>
                      ))}
                    </ul>
                  </td>
                  <td className={`${styles.amt} ${styles.amountCell} ${styles.cellAmount}`} data-label="Amount">
                    <strong>{formatINR(o.total)}</strong>
                    {toAmount(o.listTotal) > toAmount(o.total) && (
                      <s title="List price before negotiation">{formatINR(o.listTotal)}</s>
                    )}
                  </td>
                  <td className={`${styles.amt} ${styles.cellDue}`} data-label="Payment">
                    {toAmount(o.due) > 0 ? (
                      <span className={styles.dueOpen}>{formatINR(o.due)}</span>
                    ) : (
                      <span className={styles.duePaid}>Paid</span>
                    )}
                  </td>
                  <td className={styles.cellStatus} data-label="Delivery" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <select
                      className={`${styles.select} ${styles[`order_${o.status}`] || ""}`}
                      value={o.status}
                      onChange={(e) => orders.setStatus(o.id, e.target.value)}
                      aria-label={`Status for ${o.orderNo}`}
                    >
                      {ORDER_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className={styles.rowChevron} aria-hidden="true"><ChevronIcon /></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className={styles.dim}>
        Status: {ORDER_STATUSES.map((s) => statusLabel(s.value)).join(" → ")}. Cancelled orders stay listed for records.
      </p>
    </div>
  );
}

function OrderDetail({ order: o, client, statusLabel, onStatus, onEdit, onDelete, onPayment }) {
  const items = o.items || [];
  const discount = Math.max(0, toAmount(o.listTotal) - toAmount(o.total));
  const due = toAmount(o.due);
  const paid = toAmount(o.advance);
  const contact = [
    client.phone && { label: "Phone", value: <a href={`tel:${String(client.phone).replace(/\s+/g, "")}`}>{client.phone}</a> },
    client.email && { label: "Email", value: <a href={`mailto:${client.email}`}>{client.email}</a> },
    client.address && { label: "Address", value: client.address },
  ].filter(Boolean);

  return (
    <section className={styles.card}>
      <div className={styles.detailHead}>
        <div>
          <span className={styles.badge}>Order</span>
          <h2 className={styles.detailTitle}>{o.orderNo}</h2>
          <p className={styles.sub}>Placed {formatDate(o.orderDate)}</p>
        </div>
        <div className={styles.detailActions}>
          <select
            className={`${styles.select} ${styles[`order_${o.status}`] || ""}`}
            value={o.status}
            onChange={(e) => onStatus(e.target.value)}
            aria-label="Order status"
          >
            {ORDER_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button className={styles.ghostBtn} onClick={onEdit}>Edit order</button>
          <button className={styles.dangerBtn} onClick={onDelete}>Delete</button>
        </div>
      </div>

      <div className={styles.detailGrid}>
        <div className={styles.detailBlock}>
          <h3>Client</h3>
          <p className={styles.detailName}>{client.name || client.businessName || "—"}</p>
          {client.name && client.businessName && <p className={styles.dim}>{client.businessName}</p>}
          {contact.length > 0 && (
            <dl className={styles.kv}>
              {contact.map((c) => (
                <div key={c.label}><dt>{c.label}</dt><dd>{c.value}</dd></div>
              ))}
            </dl>
          )}
        </div>

        <div className={styles.detailBlock}>
          <h3>Payment</h3>
          <dl className={styles.kv}>
            {discount > 0 && <div><dt>List price</dt><dd><s>{formatINR(o.listTotal)}</s></dd></div>}
            {discount > 0 && <div><dt>Discount</dt><dd>− {formatINR(discount)}</dd></div>}
            <div><dt>Total</dt><dd><strong>{formatINR(o.total)}</strong></dd></div>
            <div><dt>Paid so far</dt><dd>{formatINR(o.advance)}</dd></div>
            <div>
              <dt>Balance due</dt>
              <dd>{due > 0 ? <span className={styles.dueOpen}>{formatINR(due)}</span> : <span className={styles.duePaid}>Paid</span>}</dd>
            </div>
            <div><dt>Status</dt><dd>{statusLabel(o.status)}</dd></div>
          </dl>
          <PaymentForm paid={paid} total={toAmount(o.total)} due={due} onSave={onPayment} />
        </div>
      </div>

      <div className={styles.detailBlock}>
        <h3>Items</h3>
        <div className={`${styles.tableWrap} ${styles.stackWrap}`}>
          <table className={`${styles.table} ${styles.stackTable}`}>
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Type</th>
                <th className={styles.amt}>List price</th>
                <th className={styles.amt}>Price</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td className={`${styles.dim} ${styles.cellHide}`}>{i + 1}</td>
                  <td className={`${styles.msgCell} ${styles.cellTitle}`}><strong>{it.name}</strong></td>
                  <td className={styles.cellSub} data-label="Type">{it.type || "Custom"}</td>
                  <td className={`${styles.amt} ${styles.cellAmount}`} data-label="List price">{formatINR(it.listPrice)}</td>
                  <td className={`${styles.amt} ${styles.cellDue}`} data-label="Price"><strong>{formatINR(it.price)}</strong></td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className={styles.dim}>No items recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {o.notes && (
        <div className={styles.detailBlock}>
          <h3>Notes</h3>
          <p className={styles.detailNotes}>{o.notes}</p>
        </div>
      )}
    </section>
  );
}

// Record money received against an order. The API keeps one running
// "advance" (amount paid so far) and works out the due balance from it.
function PaymentForm({ paid, total, due, onSave }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (nextPaid) => {
    setBusy(true);
    setError("");
    try {
      await onSave(nextPaid);
      setOpen(false);
      setAmount("");
    } catch (err) {
      setError(err?.message || "Could not save the payment.");
    }
    setBusy(false);
  };

  const submit = (e) => {
    e.preventDefault();
    const n = toAmount(amount);
    if (n <= 0) return setError("Enter the amount received.");
    if (paid + n > total) return setError(`That is more than the balance due (${formatINR(due)}).`);
    save(paid + n);
  };

  if (!open) {
    return (
      <div className={styles.payActions}>
        <button className={styles.solidBtn} onClick={() => setOpen(true)} disabled={due <= 0}>
          {due > 0 ? "Record payment" : "Fully paid"}
        </button>
        {due > 0 && (
          <button className={styles.ghostBtn} onClick={() => save(total)} disabled={busy}>
            {busy ? "Saving…" : "Mark as fully paid"}
          </button>
        )}
        {paid > 0 && (
          <button className={styles.linkBtn} type="button" onClick={() => { if (window.confirm("Reset the paid amount to ₹0?")) save(0); }} disabled={busy}>
            Reset to unpaid
          </button>
        )}
      </div>
    );
  }

  return (
    <form className={styles.payForm} onSubmit={submit}>
      <Field
        label="Amount received ₹"
        type="number"
        min="1"
        step="1"
        max={due}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        hint={`Balance due ${formatINR(due)}. New balance: ${formatINR(Math.max(0, due - toAmount(amount)))}.`}
        autoFocus
      />
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.formActions}>
        <button className={styles.solidBtn} type="submit" disabled={busy}>{busy ? "Saving…" : "Save payment"}</button>
        <button className={styles.ghostBtn} type="button" onClick={() => { setOpen(false); setError(""); }} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

function OrderForm({ order, clients, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    ...emptyOrder,
    ...order,
    clientId: String(order.clientId ?? ""),
    orderDate: String(order.orderDate || emptyOrder.orderDate).slice(0, 10),
    items: order.items?.length ? order.items.map((it) => ({ ...it })) : [{ ...emptyItem }],
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setItem = (index, patch) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === index ? { ...it, ...patch } : it)),
    }));

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (index) =>
    setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== index) : f.items }));

  const totals = computeTotals(form.items, form.advance);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clientId) {
      setError("Choose the client this order belongs to.");
      return;
    }
    if (!form.items.some((it) => String(it.name || "").trim())) {
      setError("Add at least one product or service.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave(form);
    } catch (err) {
      setError(err?.message || "Could not save the order.");
      setBusy(false);
    }
  };

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2>{order.id ? `Edit order ${order.orderNo}` : "New order"}</h2>

      <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
        <SelectField label="Client" value={form.clientId} onChange={set("clientId")} required>
          <option value="">Select a client…</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {[c.name, c.businessName].filter(Boolean).join(" · ")}
            </option>
          ))}
        </SelectField>
        <Field label="Order date" type="date" value={form.orderDate} onChange={set("orderDate")} required />
      </fieldset>

      <fieldset className={styles.group} disabled={busy}>
        <legend>Order items</legend>
        {form.items.map((it, i) => (
          <div className={styles.itemRow} key={i}>
            <Field
              label="Item"
              value={it.name}
              onChange={(e) => setItem(i, { name: e.target.value })}
              placeholder="e.g. Website design"
            />
            <Field
              label="Value ₹"
              type="number"
              min="0"
              step="1"
              value={it.price}
              onChange={(e) => setItem(i, { price: e.target.value, listPrice: e.target.value })}
            />
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={() => removeItem(i)}
              disabled={form.items.length === 1}
              aria-label="Remove item"
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" className={styles.ghostBtn} onClick={addItem}>+ Add another item</button>
      </fieldset>

      <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
        <legend>Payment</legend>
        <Field
          label="Amount paid so far ₹"
          type="number"
          min="0"
          step="1"
          value={form.advance}
          onChange={set("advance")}
          hint="Advance or payments already received. You can also record payments later from the order page."
        />
        <Field label="Notes" value={form.notes} onChange={set("notes")} textarea hint="Payment mode, delivery date, anything useful." />
      </fieldset>

      <dl className={styles.totals}>
        {totals.discount > 0 && <div><dt>List price</dt><dd><s>{formatINR(totals.listTotal)}</s></dd></div>}
        {totals.discount > 0 && <div><dt>Discount</dt><dd>− {formatINR(totals.discount)}</dd></div>}
        <div><dt>Total</dt><dd><strong>{formatINR(totals.total)}</strong></dd></div>
        <div><dt>Paid</dt><dd>{formatINR(totals.advance)}</dd></div>
        <div className={styles.totalsDue}><dt>Balance due</dt><dd>{formatINR(totals.due)}</dd></div>
      </dl>

      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.formActions}>
        <button className={styles.primaryBtn} type="submit" disabled={busy}>
          {busy ? "Saving…" : order.id ? "Save changes" : "Add order"}
        </button>
        <button className={styles.ghostBtn} type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}
